import { canonicalPluginAppID, pluginAppSessionHref, type RuntimePluginApp } from "@/plugins/runtime"

export const PLUGIN_PANEL_TAB_PREFIX = "plugin-app://"
export const OPEN_PLUGIN_PANEL_TAB_EVENT = "openanalyst:open-plugin-panel-tab"
export const PLUGIN_PANEL_POPOUT_EVENT = "openanalyst:plugin-panel-popout"
export const PLUGIN_PANEL_OPEN_TABS_EVENT = "openanalyst:plugin-panel-open-tabs"
const PENDING_PLUGIN_PANEL_TAB_KEY = "openanalyst.pluginPanel.pendingTab.v1"
// Keep the old storage key so existing sessions migrate without losing open tabs.
const PLUGIN_PANEL_OPEN_APPS_KEY = "openanalyst.pluginPanel.priorityApps.v1"
const PLUGIN_PANEL_POPOUT_KEY = "openanalyst.pluginPanel.popouts.v1"
const PLUGIN_PANEL_OPEN_TABS_KEY = "openanalyst.pluginPanel.openTabs.v1"
const PLUGIN_PANEL_ACTIVE_SURFACE_KEY = "openanalyst.pluginPanel.activeSurface.v1"
const PLUGIN_PANEL_SELECTION_KEY = "openanalyst.pluginPanel.selection.v1"
const PLUGIN_PANEL_POPOUT_STALE_MS = 60_000
const PLUGIN_PANEL_POPOUT_HEARTBEAT_MS = 15_000

type PluginPanelTabRequest = {
  directory: string
  pluginID: string
}

type PluginPanelPopoutState = PluginPanelTabRequest & {
  sessionID?: string
  openedAt: number
}

type PluginPanelOpenTabsState = {
  ids: string[]
  active?: string
}

export function pluginPanelTab(id: string) {
  return `${PLUGIN_PANEL_TAB_PREFIX}${encodeURIComponent(canonicalPluginAppID(id))}`
}

export function pluginPanelTabID(tab: string) {
  if (!tab.startsWith(PLUGIN_PANEL_TAB_PREFIX)) return
  const raw = tab.slice(PLUGIN_PANEL_TAB_PREFIX.length)
  if (!raw) return
  return canonicalPluginAppID(decodeURIComponent(raw))
}

export function pluginPanelSessionRoute(input: {
  dir: string
  pluginID: string
  sessionID?: string
  popout?: boolean
  search?: string | URLSearchParams
}) {
  return pluginAppSessionHref({
    dir: input.dir,
    id: input.pluginID,
    ...(input.sessionID ? { sessionID: input.sessionID } : {}),
    ...(input.popout ? { popout: input.popout } : {}),
    ...(input.search ? { search: input.search } : {}),
  })
}

export function pluginPanelClosedSearch<T extends { pluginApp?: string; pluginExpanded?: string; pluginView?: string }>(
  search: T,
): Omit<T, "pluginApp" | "pluginExpanded" | "pluginView"> & {
  pluginApp?: undefined
  pluginExpanded?: undefined
  pluginView?: undefined
} {
  return { ...search, pluginApp: undefined, pluginExpanded: undefined, pluginView: undefined }
}

export function isPluginPanelTab(tab: string) {
  return pluginPanelTabID(tab) !== undefined
}

export function pluginPanelAppEnabled(id: string) {
  return canonicalPluginAppID(id).length > 0
}

export function availablePluginPanelApps(input: { apps: RuntimePluginApp[]; openedTabs: string[] }) {
  const opened = new Set(input.openedTabs.flatMap((tab) => pluginPanelTabID(tab) ?? []))
  const seen = new Set<string>()
  return input.apps.flatMap((app) => {
    const id = canonicalPluginAppID(app.id)
    if (!pluginPanelAppEnabled(id) || opened.has(id) || seen.has(id)) return []
    seen.add(id)
    return [{ ...app, id }]
  })
}

function openTabsMap() {
  const item = storage()?.getItem(PLUGIN_PANEL_OPEN_TABS_KEY)
  if (!item) return {} as Record<string, PluginPanelOpenTabsState>
  try {
    const parsed = JSON.parse(item)
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) return {}
    return parsed as Record<string, PluginPanelOpenTabsState>
  } catch {
    storage()?.removeItem(PLUGIN_PANEL_OPEN_TABS_KEY)
    return {}
  }
}

export function pluginPanelOpenTabs(directory: string): PluginPanelOpenTabsState {
  const item = openTabsMap()[directory]
  if (!item || !Array.isArray(item.ids)) return { ids: [] }
  const ids = [...new Set(item.ids.map(canonicalPluginAppID).filter(pluginPanelAppEnabled))]
  const active = item.active && ids.includes(canonicalPluginAppID(item.active)) ? canonicalPluginAppID(item.active) : undefined
  return { ids, active }
}

// Restoring a session panel must respect an explicitly requested app (side-rail click, deep link,
// `?pluginApp=`): the stored tabs come back, the requested app is added if missing, and it — not the
// previously stored active tab — is the one on screen.
export function pluginPanelRestoreState(input: { directory: string; requested?: string }): PluginPanelOpenTabsState {
  const stored = pluginPanelOpenTabs(input.directory)
  const requested = input.requested ? canonicalPluginAppID(input.requested) : ""
  if (!requested || !pluginPanelAppEnabled(requested)) return stored
  const ids = stored.ids.includes(requested) ? stored.ids : [...stored.ids, requested]
  return { ids, active: requested }
}

export function setPluginPanelOpenTabs(directory: string, ids: string[], active?: string) {
  const clean = [...new Set(ids.map(canonicalPluginAppID).filter(pluginPanelAppEnabled))]
  const next = openTabsMap()
  if (!clean.length) {
    delete next[directory]
  } else {
    const activeID = active && clean.includes(canonicalPluginAppID(active)) ? canonicalPluginAppID(active) : clean[0]
    next[directory] = { ids: clean, ...(activeID ? { active: activeID } : {}) }
  }
  storage()?.setItem(PLUGIN_PANEL_OPEN_TABS_KEY, JSON.stringify(next))
  emitOpenTabsChange()
}

// ── active surface within an open app ──────────────────────────────────────────
// A sidecar app can report which of its OWN surfaces (deck, whiteboard, landing page, …) the
// user has on screen. The engine uses it to keep only that surface's tools for the app. Stored
// per (directory, appId) in sessionStorage, mirroring the open-tabs state.
function activeSurfaceMap(): Record<string, Record<string, string>> {
  const item = storage()?.getItem(PLUGIN_PANEL_ACTIVE_SURFACE_KEY)
  if (!item) return {}
  try {
    const parsed = JSON.parse(item)
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) return {}
    return parsed as Record<string, Record<string, string>>
  } catch {
    storage()?.removeItem(PLUGIN_PANEL_ACTIVE_SURFACE_KEY)
    return {}
  }
}

export function setPluginPanelActiveSurface(directory: string, pluginID: string, surface: string) {
  const dir = directory.trim()
  const id = canonicalPluginAppID(pluginID)
  if (!dir || !id) return
  const clean = surface.trim()
  const next = activeSurfaceMap()
  const forDir = { ...(next[dir] ?? {}) }
  if (clean) forDir[id] = clean
  else delete forDir[id]
  if (Object.keys(forDir).length) next[dir] = forDir
  else delete next[dir]
  storage()?.setItem(PLUGIN_PANEL_ACTIVE_SURFACE_KEY, JSON.stringify(next))
}

export function pluginPanelActiveSurface(directory: string, pluginID: string): string | undefined {
  const forDir = activeSurfaceMap()[directory.trim()]
  const surface = forDir?.[canonicalPluginAppID(pluginID)]
  return typeof surface === "string" && surface ? surface : undefined
}

// ── selected element(s) within an open app ─────────────────────────────────────
// A canvas app can report which element(s) the user has selected (highlighted). The engine puts
// them into the main-chat context so the chat can act on exactly those (e.g. an "edit selection"
// tool). Stored per (directory, appId), mirroring the active-surface state.
export type PluginPanelSelectedElement = { id: string; summary: string }

function selectionMap(): Record<string, Record<string, PluginPanelSelectedElement[]>> {
  const item = storage()?.getItem(PLUGIN_PANEL_SELECTION_KEY)
  if (!item) return {}
  try {
    const parsed = JSON.parse(item)
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) return {}
    return parsed as Record<string, Record<string, PluginPanelSelectedElement[]>>
  } catch {
    storage()?.removeItem(PLUGIN_PANEL_SELECTION_KEY)
    return {}
  }
}

export function setPluginPanelSelection(directory: string, pluginID: string, elements: PluginPanelSelectedElement[]) {
  const dir = directory.trim()
  const id = canonicalPluginAppID(pluginID)
  if (!dir || !id) return
  const clean = (Array.isArray(elements) ? elements : [])
    .filter((e): e is PluginPanelSelectedElement => !!e && typeof e.id === "string" && typeof e.summary === "string")
    .slice(0, 24)
  const next = selectionMap()
  const forDir = { ...(next[dir] ?? {}) }
  if (clean.length) forDir[id] = clean
  else delete forDir[id]
  if (Object.keys(forDir).length) next[dir] = forDir
  else delete next[dir]
  storage()?.setItem(PLUGIN_PANEL_SELECTION_KEY, JSON.stringify(next))
}

export function pluginPanelSelection(directory: string, pluginID: string): PluginPanelSelectedElement[] {
  const forDir = selectionMap()[directory.trim()]
  const sel = forDir?.[canonicalPluginAppID(pluginID)]
  return Array.isArray(sel) ? sel : []
}

function storage() {
  if (typeof window === "undefined") return
  return window.sessionStorage
}

function sharedStorage() {
  if (typeof window === "undefined") return
  return window.localStorage
}

function emitOpenTabsChange() {
  if (typeof window === "undefined") return
  window.dispatchEvent(new CustomEvent(PLUGIN_PANEL_OPEN_TABS_EVENT))
}

function normalizeRequest(input: PluginPanelTabRequest): PluginPanelTabRequest | undefined {
  const directory = input.directory.trim()
  const pluginID = canonicalPluginAppID(input.pluginID)
  if (!directory || !pluginPanelAppEnabled(pluginID)) return
  return { directory, pluginID }
}

export function pluginPanelTabRequestFromEvent(event: Event): PluginPanelTabRequest | undefined {
  if (typeof CustomEvent === "undefined" || !(event instanceof CustomEvent)) return
  const detail = event.detail
  if (!detail || typeof detail !== "object") return
  const directory = (detail as { directory?: unknown }).directory
  const pluginID = (detail as { pluginID?: unknown }).pluginID
  if (typeof directory !== "string" || typeof pluginID !== "string") return
  return normalizeRequest({ directory, pluginID })
}

export function emitOpenPluginPanelTab(input: PluginPanelTabRequest) {
  const detail = normalizeRequest(input)
  if (!detail || typeof window === "undefined") return
  window.dispatchEvent(new CustomEvent(OPEN_PLUGIN_PANEL_TAB_EVENT, { detail }))
}

export function queueOpenPluginPanelTab(input: PluginPanelTabRequest) {
  const detail = normalizeRequest(input)
  if (!detail) return
  storage()?.setItem(PENDING_PLUGIN_PANEL_TAB_KEY, JSON.stringify({ ...detail, createdAt: Date.now() }))
}

export function consumeQueuedPluginPanelTab(directory: string) {
  const item = storage()?.getItem(PENDING_PLUGIN_PANEL_TAB_KEY)
  if (!item) return
  try {
    const parsed = JSON.parse(item) as Partial<PluginPanelTabRequest> & { createdAt?: unknown }
    if (typeof parsed.directory !== "string" || typeof parsed.pluginID !== "string") return
    const detail = normalizeRequest({ directory: parsed.directory, pluginID: parsed.pluginID })
    if (!detail || detail.directory !== directory || typeof parsed.createdAt !== "number") return
    if (Date.now() - parsed.createdAt > 60_000) {
      storage()?.removeItem(PENDING_PLUGIN_PANEL_TAB_KEY)
      return
    }
    storage()?.removeItem(PENDING_PLUGIN_PANEL_TAB_KEY)
    return detail.pluginID
  } catch {
    storage()?.removeItem(PENDING_PLUGIN_PANEL_TAB_KEY)
  }
}

function openAppsMap() {
  const item = storage()?.getItem(PLUGIN_PANEL_OPEN_APPS_KEY)
  if (!item) return {} as Record<string, string[]>
  try {
    const parsed = JSON.parse(item)
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) return {}
    return parsed as Record<string, string[]>
  } catch {
    return {}
  }
}

export function setPluginPanelOpenApps(directory: string, ids: string[]) {
  const clean = [...new Set(ids.map(canonicalPluginAppID).filter((id) => id && pluginPanelAppEnabled(id)))]
  const next = openAppsMap()
  if (clean.length) next[directory] = clean
  else delete next[directory]
  storage()?.setItem(PLUGIN_PANEL_OPEN_APPS_KEY, JSON.stringify(next))
}

export function pluginPanelOpenApps(directory: string) {
  const ids = openAppsMap()[directory]
  if (!Array.isArray(ids)) return []
  return ids.filter((id): id is string => typeof id === "string").filter(pluginPanelAppEnabled)
}

export function pluginPanelAppsSystemContext(
  ids: string[],
  activeId?: string,
  activeSurface?: string,
  selection?: PluginPanelSelectedElement[],
) {
  const clean = [...new Set(ids.map(canonicalPluginAppID).filter((id) => id && pluginPanelAppEnabled(id)))]
  if (!clean.length) return
  const active = activeId ? canonicalPluginAppID(activeId) : undefined
  const surface = active && clean.includes(active) && activeSurface?.trim() ? activeSurface.trim() : undefined
  const sel = active && clean.includes(active) && selection && selection.length ? selection.slice(0, 24) : undefined
  return [
    "# Open Plugin App Priority Container",
    `Open plugin app ids: ${clean.join(", ")}`,
    ...(active && clean.includes(active)
      ? [
          `Active app (on screen — HIGHEST priority): ${active}`,
          `When the user refers to an app WITHOUT naming one — "this app", "the app", "change the UI", "fix this", "improve it" — they mean the active on-screen app (${active}), NOT whichever app the previous turns happened to be about. The focused tab wins over conversation recency. If you genuinely think they mean a different open app, ask before acting.`,
          `A custom app's start/empty state, title bar, and Start/Stop/Open/Refresh controls are host-provided chrome — they are bundled and NOT files in the app folder, so never try to read or edit them. The editable UI is the app's own ui/ files (or its live localhost UI for backend/self-hosted apps).`,
        ]
      : []),
    ...(surface
      ? [
          `Active surface: ${surface}`,
          "The active app is showing this surface. Only that surface's tools are in your tool list this turn — do not ask for tools of a surface the user has not opened.",
        ]
      : []),
    ...(sel
      ? [
          `Selected elements (${sel.length}) — the user has these highlighted on ${active}'s canvas; the ${active}_edit_selection tool changes exactly these:`,
          ...sel.map((e) => `- ${e.id}: ${e.summary}`),
        ]
      : []),
    "These apps are open in the session right panel. EVERY open app counts — an app does not have to be the active/on-screen tab for you to use it; being open is enough.",
    "For plugin-app work, inspect this priority container first: .openanalyst/plugins/<id>/ with Skills, Agents, Tools capability metadata, Scripts, Resources, memory, knowledge, context, and learnings.",
    "Each open app's OWN tools are already in your tool list for this turn (its `<id>_*` tools) — CALL THEM to read or act on that app's data. They run against the app's own connected backend and local cache. When more than one app is open, prefer the ACTIVE app's tools first, then the other open apps'. For example an open Shopify app exposes shopify_* tools (shopify_dashboard, shopify_orders, shopify_products, shopify_customers, shopify_status) that read its synced orders, products and customers from the project's local cache — use those to answer, do not tell the user the store is 'not connected' or ask them to connect an integration. The app being open IS the connection.",
    "Open apps are priority context, not an access wall. If another installed app (not open) is needed, inspect its project folder through core tools and say it is not open in the right panel.",
    "Only call tools that are actually present in your tool list; do not invent tool names.",
  ].join("\n")
}

export function pluginPanelOpenAppsSystemContext(directory: string) {
  // Use the open-tabs state: `ids` is every open tab (all of them count), `active` is the tab on
  // screen (priority). Both flow into the context so the engine can use any open app's tools and
  // prefers the active one.
  const { ids, active } = pluginPanelOpenTabs(directory)
  const open = ids.length ? ids : pluginPanelOpenApps(directory)
  const activeSurface = active ? pluginPanelActiveSurface(directory, active) : undefined
  const selection = active ? pluginPanelSelection(directory, active) : undefined
  return pluginPanelAppsSystemContext(open, active, activeSurface, selection)
}

function popoutKey(directory: string, pluginID: string) {
  return `${directory}\n${canonicalPluginAppID(pluginID)}`
}

function readPopoutMap() {
  const item = sharedStorage()?.getItem(PLUGIN_PANEL_POPOUT_KEY)
  if (!item) return {} as Record<string, PluginPanelPopoutState>
  try {
    const parsed = JSON.parse(item)
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) return {}
    return parsed as Record<string, PluginPanelPopoutState>
  } catch {
    sharedStorage()?.removeItem(PLUGIN_PANEL_POPOUT_KEY)
    return {}
  }
}

function writePopoutMap(next: Record<string, PluginPanelPopoutState>) {
  sharedStorage()?.setItem(PLUGIN_PANEL_POPOUT_KEY, JSON.stringify(next))
}

function emitPopoutChange() {
  if (typeof window === "undefined") return
  window.dispatchEvent(new CustomEvent(PLUGIN_PANEL_POPOUT_EVENT))
}

export function pluginPanelPoppedOutApps(directory: string) {
  const now = Date.now()
  return Object.values(readPopoutMap())
    .filter((item) => item.directory === directory && now - item.openedAt < PLUGIN_PANEL_POPOUT_STALE_MS)
    .map((item) => item.pluginID)
    .filter(pluginPanelAppEnabled)
}

export function pluginPanelAppPoppedOut(input: PluginPanelTabRequest) {
  const detail = normalizeRequest(input)
  if (!detail) return false
  const item = readPopoutMap()[popoutKey(detail.directory, detail.pluginID)]
  return Boolean(item && Date.now() - item.openedAt < PLUGIN_PANEL_POPOUT_STALE_MS)
}

export function setPluginPanelAppPoppedOut(input: PluginPanelTabRequest & { sessionID?: string; active: boolean }) {
  const detail = normalizeRequest(input)
  if (!detail) return
  const next = readPopoutMap()
  const key = popoutKey(detail.directory, detail.pluginID)
  if (input.active) {
    next[key] = {
      ...detail,
      ...(input.sessionID ? { sessionID: input.sessionID } : {}),
      openedAt: Date.now(),
    }
  } else {
    delete next[key]
  }
  writePopoutMap(next)
  emitPopoutChange()
}

export function registerPluginPanelPopoutWindow(input: { directory: string; pluginIDs: string[]; sessionID?: string }) {
  const directory = input.directory.trim()
  const pluginIDs = [...new Set(input.pluginIDs.map(canonicalPluginAppID).filter(pluginPanelAppEnabled))]
  if (!directory || !pluginIDs.length) return () => {}

  const markActive = () =>
    pluginIDs.forEach((pluginID) =>
      setPluginPanelAppPoppedOut({
        directory,
        pluginID,
        ...(input.sessionID ? { sessionID: input.sessionID } : {}),
        active: true,
      }),
    )

  markActive()

  let heartbeat: number | undefined
  let closed = false
  const close = () => {
    if (closed) return
    closed = true
    if (heartbeat !== undefined && typeof window !== "undefined") window.clearInterval(heartbeat)
    pluginIDs.forEach((pluginID) => setPluginPanelAppPoppedOut({ directory, pluginID, active: false }))
  }

  if (typeof window === "undefined") return close

  heartbeat = window.setInterval(markActive, PLUGIN_PANEL_POPOUT_HEARTBEAT_MS)
  const events = ["pagehide", "beforeunload", "unload"] as const
  events.forEach((event) => window.addEventListener(event, close))
  return () => {
    events.forEach((event) => window.removeEventListener(event, close))
    close()
  }
}

export function listenPluginPanelPopouts(listener: () => void) {
  if (typeof window === "undefined") return () => {}
  const onStorage = (event: StorageEvent) => {
    if (event.key === PLUGIN_PANEL_POPOUT_KEY) listener()
  }
  window.addEventListener(PLUGIN_PANEL_POPOUT_EVENT, listener)
  window.addEventListener("storage", onStorage)
  return () => {
    window.removeEventListener(PLUGIN_PANEL_POPOUT_EVENT, listener)
    window.removeEventListener("storage", onStorage)
  }
}

export function listenPluginPanelOpenTabs(listener: () => void) {
  if (typeof window === "undefined") return () => {}
  const onStorage = (event: StorageEvent) => {
    if (event.key === PLUGIN_PANEL_OPEN_TABS_KEY) listener()
  }
  window.addEventListener(PLUGIN_PANEL_OPEN_TABS_EVENT, listener)
  window.addEventListener("storage", onStorage)
  return () => {
    window.removeEventListener(PLUGIN_PANEL_OPEN_TABS_EVENT, listener)
    window.removeEventListener("storage", onStorage)
  }
}
